import { CommonModule, DatePipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, QueryList, Renderer2, ViewChildren, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SupplyDelivery } from 'fhir/r4';
import { catchError, map, of, take } from 'rxjs';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { AddSupplyDeliveryComponent } from '../add-supply-delivery/add-supply-delivery.component';
import { SupplyStockUpdateDialogComponent } from './supply-stock-update-dialog.component';
import { AdmittedPatientsComponent } from '../admitted-patients/admitted-patients.component';
import { PatientAddressPipe } from '../shared/pipes/patient-address.pipe';
import { PatientAgePipe } from '../shared/pipes/patient-age.pipe';
import { PatientContactsPipe } from '../shared/pipes/patient-contacts.pipe';
import { PatientNamePipe } from '../shared/pipes/patient-name.pipe';
import { EmphasisDirective } from '../shared/directives/emphasis.directive';
import { DetailzViewzComponent } from '../detailz-viewz/detailz-viewz.component';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { UtilityService } from '../shared/utility.service';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { ChipsDirective } from '../chips.directive';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { SupplyStockRow, SupplyStockSearchService } from './supply-stock-search.service';
import { ChargeItemDefinitionSearchService } from '../shared/charge-item-definition-search.service';

// Local type is now imported from SupplyStockSearchService

@Component({
    selector: 'app-supply-stock',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatDividerModule,
        MatTableModule,
        MatMenuModule,
        MatProgressSpinnerModule,
        EmptyStateComponent,
        DatePipe,
        ChipsDirective,
        EmphasisDirective,
        MatTooltipModule,
        TableHeaderComponent,
        MatPaginatorModule
    ],
    templateUrl: './supply-stock.component.html',
    styleUrl: './supply-stock.component.scss',
    providers: [PatientNamePipe, PatientContactsPipe, PatientAgePipe, PatientAddressPipe],
})
export class SupplyStockComponent extends AdmittedPatientsComponent {
    loading = false;
    @ViewChildren('substanceCell', { read: ElementRef }) substanceCells!: QueryList<ElementRef<HTMLElement>>;
    @ViewChildren('overlayActions', { read: ElementRef }) overlayActionsEls!: QueryList<ElementRef<HTMLElement>>;

    private readonly renderer = inject(Renderer2);
    private readonly utility = inject(UtilityService);
    private readonly searchService = inject(SupplyStockSearchService);
    private readonly pricingService = inject(ChargeItemDefinitionSearchService);
    private hoverUnlisteners: Array<() => void> = [];

    private setRowTdsZIndex(row: HTMLElement, zIndex: string): void {
        const tds = Array.from(row.querySelectorAll('td')) as HTMLElement[];
        for (const td of tds) {
            td.style.position = td.style.position || 'relative';
            td.style.zIndex = zIndex;
        }
    }

    ngAfterViewInit(): void {
        this.setupHoverEffects();
        this.subs.add(this.substanceCells.changes.subscribe(() => this.setupHoverEffects()));
        this.subs.add(this.overlayActionsEls.changes.subscribe(() => this.setupHoverEffects()));
    }

    private setupHoverEffects(): void {
        // Clean up old listeners to avoid leaks when table re-renders.
        this.hoverUnlisteners.forEach((fn) => {
            try {
                fn();
            } catch {
                // ignore
            }
        });
        this.hoverUnlisteners = [];

        const cells = this.substanceCells?.toArray?.() ?? [];
        const overlays = this.overlayActionsEls?.toArray?.() ?? [];
        const len = Math.min(cells.length, overlays.length);
        if (!len) return;

        for (let i = 0; i < len; i++) {
            const cellEl = cells[i]?.nativeElement;
            const overlayEl = overlays[i]?.nativeElement;
            if (!cellEl || !overlayEl) continue;

            // Ensure predictable initial state.
            overlayEl.style.opacity = overlayEl.style.opacity || '0';

            const rowEl = cellEl.closest('tr') as HTMLElement | null;
            if (rowEl) {
                rowEl.style.position = 'relative';
                cellEl.style.position = 'unset';
                overlayEl.style.position = 'absolute';
                // Default state (not hovered)
                this.setRowTdsZIndex(rowEl, '99999999999');
                overlayEl.style.zIndex = '99999999999';
                overlayEl.style.height = `${rowEl.offsetHeight - 5}px`;
                overlayEl.style.width = `${rowEl.offsetWidth - 5}px`;
            }

            this.hoverUnlisteners.push(
                this.renderer.listen(cellEl, 'mouseenter', () => {
                    overlayEl.style.opacity = '0.3';
                    const row = cellEl.closest('tr') as HTMLElement | null;
                    if (row) {
                        // Requested behavior: lower z-index on hover
                        this.setRowTdsZIndex(row, '1');
                        overlayEl.style.height = `${row.offsetHeight - 5}px`;
                        overlayEl.style.width = `${row.offsetWidth - 5}px`;
                    }
                })
            );

            this.hoverUnlisteners.push(
                this.renderer.listen(cellEl, 'mouseleave', () => {
                    overlayEl.style.opacity = '0';
                    const row = cellEl.closest('tr') as HTMLElement | null;
                    if (row) {
                        // Requested behavior: restore high z-index when leaving
                        this.setRowTdsZIndex(row, '99999999999');
                    }
                })
            );
        }
    }

    override displayedColumns: string[] = [
        'substance',
        'totalRemaining',
        // 'price',
        'received',
        'lastUpdated',
        'status',
        'actions',
    ];

    selected?: SupplyStockRow;

    private readonly detailsBuilder: DetailsBuilderObject = {
        resourceName: 'Supply Stock',
        resourceIcon: 'inventory_2',
        specialHeader: {
            strongSectionKey: 'Substance',
            iconSectionKeys: ['status', 'low-stock'],
            contentSectionKeys: ['Remaining', 'Packages remaining', 'Last updated'],
        },
        groups: [
            {
                groupName: 'Stock',
                groupIcon: 'inventory',
                groupKeys: [
                    'Substance',
                    'Remaining',
                    'Dispensable unit',
                    'Packages remaining',
                    'Package name',
                    'Units per package',
                    'Packages received',
                    'Last updated',
                ],
            },
            {
                groupName: 'FHIR',
                groupIcon: 'data_object',
                groupKeys: ['id', 'status', 'occurrenceDateTime', 'occurrencePeriod', 'supplier', 'destination', 'receiver'],
            },
        ],
    };

    canEditStock(): boolean {
        return this.auth.can('supplyDelivery' as any, 'editStock');
    }

    override ngOnInit(): void {
        this.computePermissions('supplyDelivery' as any);
        this.searchService.init();

        this.tableDataSource = this.searchService.dataSource as any;
        this.loading$ = this.searchService.loading$;
        this.total$ = this.searchService.total$;

        this.subs.add(
            this.dataSource.connect().subscribe((rows: any[]) => {
                if (!this.selected && rows.length) {
                    this.selected = rows[0];
                    return;
                }

                const selectedId = this.selected?.id;
                if (selectedId) {
                    const found = rows.find((r: any) => r.id === selectedId);
                    if (found) {
                        this.selected = found;
                    } else if (rows.length) {
                        this.selected = rows[0];
                    } else {
                        this.selected = undefined;
                    }
                }
            })
        );
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.searchService.destroy();
    }

    onSearch(criteria: { term: string; field: string | null }): void {
// $#$        // /* alert('Search criteria: ' + JSON.stringify(criteria) */+ '\n\n'+'search term' + criteria.term);
        this.searchService.search({ itemName: criteria.term });
    }

    onPageChange(event: PageEvent): void {
        this.searchService.changePage(event.pageIndex);
    }

    loading$ = this.searchService.loading$;
    total$ = this.searchService.total$;
    get dataSource() { return this.searchService.dataSource; }

    reload(): void {
        this.searchService.refresh();
    }

    selectRow(row: SupplyStockRow): void {
        this.selected = row;
    }

    showStockDetails(row: SupplyStockRow): void {
        if (!row) return;

        const resourceData: any = {
            resourceType: row.rawResource?.resourceType,
            id: row.rawResource?.id,
            status: (row.rawResource as any)?.status,
            occurrenceDateTime: (row.rawResource as any)?.occurrenceDateTime,
            occurrencePeriod: (row.rawResource as any)?.occurrencePeriod,
            supplier: (row.rawResource as any)?.supplier,
            destination: (row.rawResource as any)?.destination,
            receiver: (row.rawResource as any)?.receiver,

            ['Substance']: row.substance,
            ['Remaining']: row.totalRemaining,
            ['Dispensable unit']: row.dispensableUnit,
            ['Packages remaining']: row.packagesRemaining,
            ['Package name']: row.packageName,
            ['Units per package']: row.quantityPerPackage,
            ['Packages received']: row.packagesReceived,
            ['Last updated']: row.lastUpdated,
            ...(row.lowStockLabel ? { ['Low stock']: row.lowStockLabel } : {}),
            ...(row.lowStockThreshold !== undefined
                ? { ['Low stock threshold']: `${row.lowStockThreshold}${row.lowStockUnit ? ` ${row.lowStockUnit}` : ''}` }
                : {}),
            ...(row.lowStock ? { ['low-stock']: row.lowStockLabel ?? 'Low stock alert' } : {}),
            ...(row.chargeItemDefinition ? { ['Pricing']: row.priceDisplay } : {}),
        };

        this.dialog.open(DetailzViewzComponent, {
            maxHeight: '93vh',
            maxWidth: '90vh',
            data: {
                resourceData,
                detailsBuilderObject: this.detailsBuilder,
                showOnlyKeysWithValues: true,
            },
        });
    }

    openUpdateDialog(row: SupplyStockRow): void {
        const ref = this.dialog.open(SupplyStockUpdateDialogComponent, {
            width: '520px',
            maxWidth: '95vw',
            data: { resource: row.rawResource },
        });

        ref.afterClosed().subscribe((result: any) => {
            if (result) this.searchService.refresh();
        });
    }

    openAddDialog(): void {
        const ref = this.dialog.open(AddSupplyDeliveryComponent, {
            maxWidth: '650px',
            maxHeight: '90vh',
            width: '100%',
        });

        ref.afterClosed().subscribe((result: any) => {
            if (result) this.searchService.refresh();
        });
    }

}
